import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { OfficeShiftRow, ShiftReport } from '@dastarhan/contracts';
import { actLines, formatMoney, xReportLines, zReportLines } from '@dastarhan/core';
import { useAskSheet, useNote } from './ui/ask';

/*
 * Офис → Смены. Владелец видит смены точки и закрывает висящие
 * дистанционно — но по-настоящему: пересчитал ящик (сам или по
 * звонку кассиру), внёс фактическую наличность, получил «Сходится»
 * или «Недостачу» тут же. Ленты X/Z/Акт любой смены — в один клик.
 */

const T = (v: number) => formatMoney(v);

export function ShiftsScreen({ token }: { token: string }) {
  const locations = useQuery({
    queryKey: ['locations'],
    queryFn: async (): Promise<{ id: string; name: string }[]> => {
      const r = await fetch('/api/v1/menu/locations',
        { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error('locations');
      return r.json();
    },
  });
  const [locId, setLocId] = useState<string | null>(null);
  const loc = locId ?? locations.data?.[0]?.id ?? null;
  const locName = locations.data?.find((l) => l.id === loc)?.name ?? 'Dastarhan';
  if (locations.isLoading) return <p className="hint">Загружаю точки…</p>;
  if (!loc) return <p className="hint">Точек нет.</p>;
  return (
    <div className="shifts-office">
      {(locations.data?.length ?? 0) > 1 && (
        <select className="input" value={loc}
          onChange={(e) => setLocId(e.target.value)}>
          {locations.data!.map((l) => (
            <option key={l.id} value={l.id}>{l.name}</option>
          ))}
        </select>
      )}
      <ShiftsList token={token} locationId={loc} place={locName} />
    </div>
  );
}

function crossesBoundary(openedAt: string): boolean {
  const b = new Date(); b.setHours(6, 0, 0, 0);
  if (b.getTime() > Date.now()) b.setDate(b.getDate() - 1);
  return new Date(openedAt) < b;
}

function daysOpen(openedAt: string): number {
  return Math.floor((Date.now() - new Date(openedAt).getTime()) / 86_400_000);
}

function ShiftsList({ token, locationId, place }:
  { token: string; locationId: string; place: string }) {
  const { ask, sheet } = useAskSheet();
  const { note, noteView } = useNote();
  const qc = useQueryClient();
  const H = { Authorization: `Bearer ${token}` };
  const list = useQuery({
    queryKey: ['office-shifts', locationId],
    queryFn: async (): Promise<OfficeShiftRow[]> => {
      const r = await fetch(`/api/v1/shifts/office/list?locationId=${locationId}`,
        { headers: H });
      if (!r.ok) throw new Error('list');
      return r.json();
    },
  });
  const [tape, setTape] = useState<string[] | null>(null);
  const [closing, setClosing] = useState<string | null>(null);
  const [cash, setCash] = useState('');
  const [verdict, setVerdict] = useState<string | null>(null);

  const split = useMutation({
    mutationFn: async (id: string) => {
      const r = await fetch(`/api/v1/shifts/office/${id}/split-close`, {
        method: 'POST', headers: H,
      });
      if (!r.ok) throw new Error('split');
      return r.json() as Promise<{
        oldNumber: number; newNumber: number;
        movedChecks: number; carryCash: number;
      }>;
    },
    onSuccess: (d) => {
      setVerdict('Смена №' + d.oldNumber
        + ' закрыта границей 06:00 (без пересчёта). Сегодняшние чеки ('
        + d.movedChecks + ') переехали в новую смену №' + d.newNumber
        + '; ящик перетёк: ' + T(d.carryCash)
        + '. Вечером закройте №' + d.newNumber + ' с пересчётом.');
      void qc.invalidateQueries({ queryKey: ['office-shifts', locationId] });
    },
  });

  const close = useMutation({
    mutationFn: async (p: { id: string; actualCash: number }) => {
      const r = await fetch(`/api/v1/shifts/office/${p.id}/close`, {
        method: 'POST',
        headers: { ...H, 'Content-Type': 'application/json' },
        body: JSON.stringify({ actualCash: p.actualCash }),
      });
      if (!r.ok) throw new Error('close');
      return r.json() as Promise<{ expectedCash: number; diff: number }>;
    },
    onSuccess: (d) => {
      setVerdict(d.diff === 0
        ? `Сходится: ${T(d.expectedCash)}`
        : d.diff < 0
          ? `Недостача ${T(-d.diff)} (ожидалось ${T(d.expectedCash)})`
          : `Излишек ${T(d.diff)} (ожидалось ${T(d.expectedCash)})`);
      setClosing(null); setCash('');
      void qc.invalidateQueries({ queryKey: ['office-shifts', locationId] });
    },
  });

  const showDayTape = async (date: string, kind: 'x' | 'act') => {
    const r = await fetch(
      `/api/v1/shifts/office/day/${date}?locationId=${locationId}`,
      { headers: H });
    if (!r.ok) return;
    const rep = await r.json() as ShiftReport;
    setTape(kind === 'x'
      ? xReportLines(rep, place, 48)
      : actLines(rep, place, 48));
  };

  const showTape = async (id: string, kind: 'x' | 'z' | 'act',
    row: OfficeShiftRow) => {
    const r = await fetch(`/api/v1/shifts/office/${id}/report`, { headers: H });
    if (!r.ok) return;
    const rep = await r.json() as ShiftReport;
    const W = 48;
    if (kind === 'x') setTape(xReportLines(rep, place, W));
    else if (kind === 'act') setTape(actLines(rep, place, W));
    else setTape(zReportLines(rep, place, {
      actualCash: row.closingCash ?? rep.expectedCash, copy: true,
    }, W));
  };

  if (list.isLoading) return <p className="hint">Загружаю смены…</p>;
  return (
    <>
      {verdict && (
        <p className="office-verdict" onClick={() => setVerdict(null)}>
          {verdict} · нажмите, чтобы скрыть
        </p>
      )}
      <div className="office-shift-list">
        {list.data?.map((s) => (
          <div key={s.id}
            className={`office-shift-card ${s.closedAt ? '' : 'open'}`}>
            <div className="osc-info">
              <span className="osc-title">
                Смена №{s.number}
                {!s.closedAt && (
                  <em>
                    {' · открыта'}
                    {daysOpen(s.openedAt) > 0
                      ? ` ${daysOpen(s.openedAt)} дн. назад` : ''}
                  </em>
                )}
              </span>
              <span className="osc-sub">
                {new Date(s.openedAt).toLocaleString('ru-RU',
                  { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                {s.closedAt
                  ? ` — ${new Date(s.closedAt).toLocaleString('ru-RU',
                    { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}`
                  : ''}
                {' · '}{s.openedByName}
                {' · чеков '}{s.checks}{' · '}{T(s.revenue)}
              </span>
              {s.days && (
                <div className="osc-days">
                  <span className="osc-days-note">
                    Смена пересекла границу 06:00 — по дням:
                  </span>
                  {s.days.map((d) => (
                    <span key={d.date} className="osc-day-row">
                      за {d.date.slice(8, 10)}.{d.date.slice(5, 7)} —
                      {' '}{d.checks} чек(ов) · {T(d.revenue)}
                      <button className="btn tiny"
                        onClick={() => void showDayTape(d.date, 'x')}>X</button>
                      <button className="btn tiny"
                        onClick={() => void showDayTape(d.date, 'act')}>Акт</button>
                    </span>
                  ))}
                </div>
              )}
              {!s.closedAt && s.expectedCash !== null && (
                <span className="osc-expected">
                  В ящике должно быть: <b>{T(s.expectedCash)}</b>
                </span>
              )}
              {s.closedAt && s.closingCash === null && (
                <span className="osc-warn">закрыта без пересчёта</span>
              )}
            </div>
            <div className="osc-actions">
              {!s.closedAt && closing !== s.id && (
                crossesBoundary(s.openedAt) ? (
                  <button className="btn accent"
                    onClick={() => void (async () => {
                      const a = await ask({
                        title: `Разделить смену №${s.number} по границе дня`,
                        sub: 'Смена началась вчера и идёт до сих пор. Разрежем её ровно по 06:00, чтобы вчерашняя выручка попала во вчерашний день.',
                        effects: [
                          ['Вчерашняя часть', 'закроется границей, без пересчёта ящика'],
                          ['Сегодняшние чеки', 'перейдут в новую смену'],
                          ['Пересчёт ящика', 'вечером, при закрытии новой смены'],
                          ['Чеков в смене', String(s.checks)],
                        ],
                        confirmLabel: 'Разделить',
                      });
                      if (!a) return;
                      split.mutate(s.id);
                    })()}>
                    Разделить по 06:00 и закрыть вчерашнюю
                  </button>
                ) : (
                  <button className="btn accent"
                    onClick={() => { setClosing(s.id); setCash(''); }}>
                    Закрыть смену
                  </button>
                )
              )}
              {closing === s.id && (
                <div className="osc-close-form">
                  <input className="input" inputMode="numeric"
                    placeholder="Фактически в кассе, ₸"
                    value={cash}
                    onChange={(e) => setCash(e.target.value.replace(/[^\d]/g, ''))} />
                  <button className="btn accent" disabled={cash === ''}
                    onClick={() => void (async () => {
                      /* Расхождение показываем до нажатия: недостача — это
                         разговор с кассиром, и цифру надо видеть заранее. */
                      const tng = Number(cash);
                      const actual = tng * 100;
                      const expected = s.expectedCash;
                      const diff = expected === null ? null : actual - expected;
                      const effects: [string, string][] = [
                        ['Смена', `№${s.number} · ${s.openedByName}`],
                        ['Чеков', String(s.checks)],
                        ['Насчитали в ящике', `${tng.toLocaleString('ru-RU')} ₸`],
                      ];
                      if (expected !== null) {
                        effects.splice(2, 0, ['Должно быть по расчёту', T(expected)]);
                        effects.push([
                          diff === 0 ? 'Сходится' : (diff ?? 0) < 0 ? 'Недостача' : 'Излишек',
                          diff === 0 ? 'ровно' : T(Math.abs(diff ?? 0)),
                        ]);
                      }
                      const a = await ask({
                        title: `Закрыть смену №${s.number}`,
                        sub: expected === null
                          ? 'После закрытия смену не переоткрыть.'
                          : (diff === 0
                            ? 'Ящик сходится с расчётом. После закрытия смену не переоткрыть.'
                            : 'Расхождение попадёт в отчёт и в журнал — с ним потом разбираются. После закрытия смену не переоткрыть.'),
                        effects,
                        danger: diff !== null && diff !== 0,
                        confirmLabel: 'Закрыть смену',
                      });
                      if (!a) return;
                      close.mutate({ id: s.id, actualCash: actual }, {
                        onSuccess: () => note(`Смена №${s.number} закрыта`),
                      });
                    })()}>
                    Подтвердить
                  </button>
                  <button className="btn"
                    onClick={() => setClosing(null)}>Отмена</button>
                </div>
              )}
              <div className="osc-tapes">
                <button className="btn small"
                  onClick={() => void showTape(s.id, 'x', s)}>X</button>
                <button className="btn small" disabled={!s.closedAt}
                  title={s.closedAt ? 'Копия Z' : 'Z появится после закрытия'}
                  onClick={() => void showTape(s.id, 'z', s)}>Z</button>
                <button className="btn small"
                  onClick={() => void showTape(s.id, 'act', s)}>Акт</button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {tape && (
        <div className="tape-modal" role="dialog" aria-modal="true">
          <div className="tape-modal-paper"><pre>{tape.join('\n')}</pre></div>
          <div className="tape-modal-actions">
            <button className="btn" onClick={() => window.print()}>
              🖨 Печать
            </button>
            <button className="btn accent" onClick={() => setTape(null)}>
              Закрыть
            </button>
          </div>
        </div>
      )}
      {sheet}
      {noteView}
    </>
  );
}
